// biome-ignore-all lint/suspicious/noArrayIndexKey: FilterNode and FilterRule have no identifiers; controlled editors follow store positions.
import { type ReactNode, useMemo } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'
import {
  MAX_BROWSE_FILTER_GROUP_RULES,
  MAX_BROWSE_FILTER_NODES,
  MAX_BROWSE_FILTER_VALUE_LENGTH,
} from '@/lib/coaching/browse-filters'
import { MOCK_FEATURED_COMPANIES, MOCK_PLANS } from '../../_mock-data/library'
import { useCoachingStore } from '../../store'
import {
  condsByKind,
  type FilterField,
  type FilterGroup,
  type FilterNode,
  type FilterRule,
  fieldKinds,
} from '../_lib/filter-engine'
import type { TypeFilter } from '../_state/contracts'

type ConditionKind = keyof typeof condsByKind

interface FieldOption {
  value: string
  label: string
}

interface FieldChoice {
  field: FilterField
  label: string
  kind: ConditionKind
  defaultValue: FilterRule['value']
  values?: readonly FieldOption[]
}

// One label per field; the kind comes from the engine's `fieldKinds` — the
// single field→kind map — so a new field cannot ship a panel entry whose
// blank default the engine fails to classify (LG-827). The mode/type-aware
// lists below pick from these, so a rule whose field is not currently
// offered still renders.
const FIELD_LABELS: Record<FilterField, string> = {
  topic: 'Topic / subtopic',
  elo: 'ELO',
  extId: 'External ID',
  plan: 'Study plan',
  difficulty: 'Difficulty',
  company: 'Company',
  title: 'Title',
  source: 'Source',
}

const toOptions = (values: readonly string[]): FieldOption[] =>
  values.map((value) => ({ value, label: value }))

const choice = (
  field: FilterField,
  values?: readonly FieldOption[],
): FieldChoice => ({
  field,
  label: FIELD_LABELS[field],
  kind: fieldKinds[field],
  defaultValue: values === undefined ? '' : (values[0]?.value ?? ''),
  values,
})

const DIFFICULTY_OPTIONS = toOptions(['Easy', 'Med', 'Hard'])

// Mock items carry the source abbreviation directly.
const MOCK_SOURCE_OPTIONS = toOptions(['LC', 'BCtCI', 'CtCI', 'GCI'])

const MOCK_FIELD_CHOICES: readonly FieldChoice[] = [
  choice('topic'),
  choice('elo'),
  choice('extId'),
  choice('plan', toOptions(MOCK_PLANS)),
  choice('difficulty', DIFFICULTY_OPTIONS),
  choice('source', MOCK_SOURCE_OPTIONS),
  choice('company', toOptions(MOCK_FEATURED_COMPANIES)),
  choice('title'),
]

// Persisted mode is type-aware: coding gets the full server-evaluated set;
// SD and behavioral content has no difficulty/source/plan/ELO data yet, so
// those pills offer only the text fields that mean something.
function persistedFieldChoices(
  typeFilter: TypeFilter,
  planOptions: readonly string[],
  sourceOptions: readonly FieldOption[],
): FieldChoice[] {
  if (typeFilter === 'sd' || typeFilter === 'behavioral') {
    return [choice('topic'), choice('title')]
  }
  return [
    choice('topic'),
    choice('difficulty', DIFFICULTY_OPTIONS),
    // Source values are live Source.slug rows fed from the server; the
    // compiler matches against slug, labels stay the familiar abbreviations.
    choice('source', sourceOptions),
    choice('plan', toOptions(planOptions)),
    choice('elo'),
    choice('extId'),
    choice('title'),
  ]
}

const fieldChoiceFrom = (
  choices: readonly FieldChoice[],
  field: FilterField,
): FieldChoice =>
  choices.find((entry) => entry.field === field) ?? choice(field)

const ruleForField = (
  choices: readonly FieldChoice[],
  field: FilterField,
  connector: FilterRule['connector'],
): FilterRule => {
  const fieldChoice = fieldChoiceFrom(choices, field)
  return {
    connector,
    field,
    condition: condsByKind[fieldChoice.kind][0],
    value: fieldChoice.defaultValue,
  }
}

const defaultRule = (connector: FilterRule['connector']): FilterRule => ({
  connector,
  field: 'topic',
  condition: condsByKind.text[0],
  value: '',
})

const isGroup = (node: FilterNode): node is FilterGroup => 'rules' in node

export const filterRuleCount = (filters: FilterNode[]): number =>
  filters.reduce(
    (count, node) => count + (isGroup(node) ? node.rules.length : 1),
    0,
  )

// Caps at the wire contract's node limit so the builder can never assemble a
// tree the tRPC schema would reject; the UI disables the add affordances at
// the same threshold, this is the belt-and-suspenders guard.
export const appendDefaultFilterRule = (filters: FilterNode[]): FilterNode[] =>
  filters.length >= MAX_BROWSE_FILTER_NODES
    ? filters
    : [...filters, defaultRule(filters.length === 0 ? 'where' : 'and')]

const normalizeTopLevelConnectors = (nodes: FilterNode[]): FilterNode[] =>
  nodes.map((node, index) => {
    if (isGroup(node)) return node
    const connector =
      index === 0 ? 'where' : node.connector === 'or' ? 'or' : 'and'
    return node.connector === connector ? node : { ...node, connector }
  })

const inputClass =
  'rounded-md border border-(--cd-border-2) bg-(--cd-inset) px-2 py-1 text-[11px] text-(--cd-text) outline-none focus:border-(--cd-amber)'

interface RuleEditorProps {
  rule: FilterRule
  labelPrefix: string
  first: boolean
  onChange: (rule: FilterRule) => void
  onRemove: () => void
  fieldChoices: readonly FieldChoice[]
}

function RuleEditor(props: RuleEditorProps): ReactNode {
  const { rule, labelPrefix, first, onChange, onRemove, fieldChoices } = props
  const ruleChoice = fieldChoiceFrom(fieldChoices, rule.field)
  // A rule can outlive its field's availability (e.g. a coding-only field
  // after switching the type pill); keep it editable in the dropdown.
  const selectableChoices = fieldChoices.some(
    (candidate) => candidate.field === rule.field,
  )
    ? fieldChoices
    : [...fieldChoices, ruleChoice]

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {first ? (
        <span className="w-14 text-right text-[11px] text-(--cd-text-muted)">
          Where
        </span>
      ) : (
        <select
          aria-label={`${labelPrefix} connector`}
          value={rule.connector === 'or' ? 'or' : 'and'}
          onChange={(event) =>
            onChange({
              ...rule,
              connector: event.target.value as 'and' | 'or',
            })
          }
          className={`${inputClass} w-14`}
        >
          <option value="and">And</option>
          <option value="or">Or</option>
        </select>
      )}

      <select
        aria-label={`${labelPrefix} field`}
        value={rule.field}
        onChange={(event) =>
          onChange(
            ruleForField(
              fieldChoices,
              event.target.value as FilterField,
              rule.connector,
            ),
          )
        }
        className={inputClass}
      >
        {selectableChoices.map((field) => (
          <option key={field.field} value={field.field}>
            {field.label}
          </option>
        ))}
      </select>

      <select
        aria-label={`${labelPrefix} condition`}
        value={rule.condition}
        onChange={(event) =>
          onChange({ ...rule, condition: event.target.value })
        }
        className={inputClass}
      >
        {condsByKind[ruleChoice.kind].map((condition) => (
          <option key={condition} value={condition}>
            {condition}
          </option>
        ))}
      </select>

      {ruleChoice.values ? (
        <select
          aria-label={`${labelPrefix} value`}
          value={String(rule.value)}
          onChange={(event) => onChange({ ...rule, value: event.target.value })}
          className={inputClass}
        >
          {ruleChoice.values.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          aria-label={`${labelPrefix} value`}
          type={ruleChoice.kind === 'number' ? 'number' : 'text'}
          maxLength={
            ruleChoice.kind === 'number'
              ? undefined
              : MAX_BROWSE_FILTER_VALUE_LENGTH
          }
          value={
            typeof rule.value === 'string' || typeof rule.value === 'number'
              ? rule.value
              : ''
          }
          onChange={(event) => onChange({ ...rule, value: event.target.value })}
          className={`${inputClass} w-36`}
        />
      )}

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="🗑 Delete filter"
            onClick={onRemove}
            className="rounded px-1.5 py-1 text-[10px] text-(--cd-text-faint) hover:text-(--cd-red)"
          >
            🗑
          </button>
        </TooltipTrigger>
        <TooltipContent>Delete {labelPrefix.toLowerCase()}</TooltipContent>
      </Tooltip>
    </div>
  )
}

export function FilterPanel(): ReactNode {
  const { curriculum } = useCoachingStore()
  const { filters, setFilters } = curriculum
  const persisted = curriculum.persisted === true
  const typeFilter = curriculum.typeFilter
  const planOptions = curriculum.planOptions
  const sourceOptions = curriculum.sourceOptions
  const fieldChoices = useMemo(
    () =>
      persisted
        ? persistedFieldChoices(
            typeFilter,
            planOptions ?? [],
            sourceOptions ?? [],
          )
        : MOCK_FIELD_CHOICES,
    [persisted, typeFilter, planOptions, sourceOptions],
  )
  const atNodeCap = filters.length >= MAX_BROWSE_FILTER_NODES

  const replaceNode = (index: number, node: FilterNode): void => {
    setFilters(
      filters.map((current, currentIndex) =>
        currentIndex === index ? node : current,
      ),
    )
  }

  const removeNode = (index: number): void => {
    setFilters(
      normalizeTopLevelConnectors(
        filters.filter((_, currentIndex) => currentIndex !== index),
      ),
    )
  }

  const addRule = (): void => {
    if (atNodeCap) return
    setFilters(appendDefaultFilterRule(filters))
  }

  const addGroup = (): void => {
    if (atNodeCap) return
    setFilters([
      ...filters,
      { connector: 'and', rules: [defaultRule('where')] },
    ])
  }

  return (
    <div
      id="library-filter-panel"
      className="rounded-[10px] border border-(--cd-border-2) bg-(--cd-panel-2) px-3 py-2.5"
    >
      <div className="space-y-2">
        {filters.map((node, nodeIndex) => {
          const first = nodeIndex === 0
          if (!isGroup(node)) {
            const ruleOrdinal = filters
              .slice(0, nodeIndex + 1)
              .filter((candidate) => !isGroup(candidate)).length
            return (
              <RuleEditor
                key={`rule-${nodeIndex}`}
                rule={node}
                labelPrefix={`Rule ${ruleOrdinal}`}
                first={first}
                onChange={(rule) => replaceNode(nodeIndex, rule)}
                onRemove={() => removeNode(nodeIndex)}
                fieldChoices={fieldChoices}
              />
            )
          }

          const groupOrdinal = filters
            .slice(0, nodeIndex + 1)
            .filter(isGroup).length
          return (
            <fieldset
              key={`group-${nodeIndex}`}
              aria-label={`Filter group ${groupOrdinal}`}
              className="flex items-start gap-2 rounded-lg border border-(--cd-border-2) bg-(--cd-inset)/50 p-2"
            >
              {first ? (
                <span className="w-14 pt-1 text-right text-[11px] text-(--cd-text-muted)">
                  Where
                </span>
              ) : (
                <select
                  aria-label="Group connector"
                  value={node.connector}
                  onChange={(event) =>
                    replaceNode(nodeIndex, {
                      ...node,
                      connector: event.target.value as 'and' | 'or',
                    })
                  }
                  className={`${inputClass} w-14`}
                >
                  <option value="and">And</option>
                  <option value="or">Or</option>
                </select>
              )}

              <div className="min-w-0 flex-1 space-y-2">
                {node.rules.map((rule, ruleIndex) => (
                  <RuleEditor
                    key={`group-${nodeIndex}-rule-${ruleIndex}`}
                    rule={rule}
                    labelPrefix={`Group rule ${ruleIndex + 1}`}
                    first={ruleIndex === 0}
                    onChange={(nextRule) =>
                      replaceNode(nodeIndex, {
                        ...node,
                        rules: node.rules.map((currentRule, currentIndex) =>
                          currentIndex === ruleIndex ? nextRule : currentRule,
                        ),
                      })
                    }
                    onRemove={() =>
                      replaceNode(nodeIndex, {
                        ...node,
                        rules: node.rules.filter(
                          (_, currentIndex) => currentIndex !== ruleIndex,
                        ),
                      })
                    }
                    fieldChoices={fieldChoices}
                  />
                ))}
                <button
                  type="button"
                  disabled={node.rules.length >= MAX_BROWSE_FILTER_GROUP_RULES}
                  onClick={() => {
                    if (node.rules.length >= MAX_BROWSE_FILTER_GROUP_RULES) {
                      return
                    }
                    replaceNode(nodeIndex, {
                      ...node,
                      rules: [
                        ...node.rules,
                        defaultRule(node.rules.length === 0 ? 'where' : 'and'),
                      ],
                    })
                  }}
                  className="text-[11px] text-(--cd-text-muted) hover:text-(--cd-text) disabled:cursor-not-allowed disabled:opacity-40"
                >
                  ＋ Add filter rule
                </button>
              </div>

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="🗑 Delete filter"
                    onClick={() => removeNode(nodeIndex)}
                    className="rounded px-1.5 py-1 text-[10px] text-(--cd-text-faint) hover:text-(--cd-red)"
                  >
                    🗑
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  Delete filter group {groupOrdinal}
                </TooltipContent>
              </Tooltip>
            </fieldset>
          )
        })}
      </div>

      <div className="mt-3 flex flex-wrap gap-4 border-t border-(--cd-border) pt-2">
        <button
          type="button"
          disabled={atNodeCap}
          onClick={addRule}
          className="text-[11px] text-(--cd-text-muted) hover:text-(--cd-text) disabled:cursor-not-allowed disabled:opacity-40"
        >
          ＋ Add filter rule
        </button>
        <button
          type="button"
          disabled={atNodeCap}
          onClick={addGroup}
          className="text-[11px] text-(--cd-text-muted) hover:text-(--cd-text) disabled:cursor-not-allowed disabled:opacity-40"
        >
          ＋ Add filter group
        </button>
        <button
          type="button"
          disabled={filters.length === 0}
          onClick={() => setFilters([])}
          className="ml-auto text-[11px] text-(--cd-text-faint) hover:text-(--cd-red) disabled:cursor-not-allowed disabled:opacity-40"
        >
          🗑 Delete filter
        </button>
      </div>
      <p className="mt-1.5 text-[9px] text-(--cd-text-faint)">
        Rules combine top-to-bottom (no precedence).
      </p>
    </div>
  )
}
